import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../components/atoms/button";
import AssessmentSectionCard from "./_components/assessment-dashboard/assessmentSectionCard";
import ZoomControls from "./_components/question/zoomControls";

import LegendItem from "./_components/legendItem";
import { getAssessmentSectionsDashboardSteps } from "./_components/ui-tour/tourStepsData";
import CustomTourProvider from "../../components/molecules/customTourProvider";

import GetHeroIcon from "../../utils/getHeroIcons";

import { useAppDispatch, useAppSelector } from "../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import AppMetaDataSlice from "@lxp-app/store/slices/appMetaDataSlice";

import { OngoingSection } from "../../types/assessmentOngoing";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import Modal from "@assessment/components/atoms/modal";
import Instructions from "./_components/instructions";
import { InstructionsArr } from "@assessment/utils/instructionsArr";
import { useLazyHeartBeatApiQuery } from "@assessment/store/slices/api/assessmentApiSlice";
import { useParams } from "react-router-dom";
import Switcher from "@lxp-app/layouts/header/switcher-new";
import { useSelector } from "react-redux";
import { RootState } from "@lxp-app/store/store";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import LanguageSwitcher from "@assessment/components/molecules/languageSwitcher";

const AssessmentDashboard = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { scheduleId } = useParams();

  const [heartBeatApi] = useLazyHeartBeatApiQuery();
  const user = useAppSelector((state) => state.UserInfoSlice.profile);

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );

  const showProctoringVideoPreview = useAppSelector(
    (state) => state.AssessmentSlice.proctoring.showProctoringVideoPreview
  );

  const preAssessmentData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData
  );

  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );

  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );

  const expandAllAfterReview = useAppSelector(
    (state) => state.AssessmentSlice.expandAll
  );
  const viewHeader: any = useSelector(
    (state: RootState) => state.AppMetaDataSlice.header
  );

  const ongoingSections = assessmentData?.ongoingSections;

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const orgbasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const [showUITour, setShowUITour] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [manuallyExpandedCards, setManuallyExpandedCards] = useState<{
    [sectionId: string]: boolean;
  }>({});

  const val = InstructionsArr({
    qpInstruction: preAssessmentData?.qpInstruction,
    scheduleInstruction: preAssessmentData?.scheduleInstruction as string
  });

  const toggleCard = (sectionId: string) => {
    setManuallyExpandedCards((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // useEffect(() => {
  //   heartBeatApiCall();
  // }, [])

  useEffect(() => {
    if (ongoingSections) {
      const allExpanded = ongoingSections.every(
        (section) => expandAll || manuallyExpandedCards[section.sectionId]
      );
      setExpandAll(allExpanded);
    }
  }, [manuallyExpandedCards, ongoingSections]);

  useEffect(() => {
    if (ongoingSections) {
      if (ongoingSections.length > 0) {
        const allOpen = ongoingSections.reduce(
          (acc, section) => {
            acc[section.sectionId] = true;
            return acc;
          },
          {} as Record<string, boolean>
        );
        setManuallyExpandedCards(allOpen);
      }
    }
  }, [ongoingSections]);

  useEffect(() => {
    if (expandAll === true) {
      return;
    }
    if (expandAllAfterReview) {
      toggleExpandAll();
    }
  }, [expandAllAfterReview]);

  useEffect(() => {
    const allCardsManuallyClosed = Object.values(manuallyExpandedCards).every(
      (val) => val === false
    );
    if (allCardsManuallyClosed) {
      setExpandAll(false);
    }
  }, [manuallyExpandedCards]);

  const toggleExpandAll = () => {
    const nextExpandAll = !expandAll;
    setExpandAll(nextExpandAll);

    if (!expandAll) {
      dispatch(AssessmentSliceActions.mutateExpandAll(false));
    }

    // Set all cards manually to expanded or collapsed
    if (nextExpandAll && ongoingSections) {
      const newManualState = ongoingSections.reduce(
        (acc, section) => {
          acc[section.sectionId] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      setManuallyExpandedCards(newManualState);
    } else {
      setManuallyExpandedCards({});
    }
  };

  // const heartBeatApiCall = async () => {
  //   try {
  //     if (!scheduleId) {
  //       throw {
  //         status: "",
  //         title: "Schedule Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     if (!ongoingId) {
  //       throw {
  //         status: "",
  //         title: "Ongoing Id Not Found",
  //         err: {},
  //         description: ""
  //       };
  //     }
  //     const heartBeatRes = await heartBeatApi({
  //       ongoingId: ongoingId,
  //       scheduleId: scheduleId
  //     })
  //     if (heartBeatRes?.data?.data?.ongoingQPDuration) {
  //       dispatch(
  //         AssessmentSliceActions.mutateTimeLeft(
  //           heartBeatRes?.data?.data?.ongoingQPDuration
  //         ))
  //     }
  //   } catch (error: any) {
  //     console.log("error", error)
  //   }
  // }

  return (
    <div>
      {videoProctoring && <div className="h-14 max-lg:hidden w-full"></div>}
      {!videoProctoring && (
        <div className="h-14 hidden md:inline-block 2xl:hidden w-full"></div>
      )}
      {/* Top */}
      <div className="flex flex-col lg:items-center justify-between w-full gap-10 lg:flex-row">
        <h1 className="text-2xl font-semibold leading-7 text-secondary text-wrap w-[40%]">
          {preAssessmentData?.title}
        </h1>
        {/* Video Proctoring button */}
        <div className="flex items-center flex-wrap flex-shrink-0 w-full gap-3 sm:gap-4 lg:gap-8 lg:w-fit">
          {videoProctoring && (
            <button
              id="toggleButton"
              type="button"
              className="flex items-center gap-4 px-3 py-2 border border-secondary/20 dark:border-secondary/30  rounded-md"
              onClick={() => {
                if (showProctoringVideoPreview)
                  dispatch(
                    AssessmentSliceActions.mutateShowProctoringVideoPreview(
                      false
                    )
                  );
                else
                  dispatch(
                    AssessmentSliceActions.mutateShowProctoringVideoPreview(
                      true
                    )
                  );
              }}
            >
              <div className="w-2 h-2 rounded-full bg-[#ff5f20] animate-pulse" />
              <span className="flex-1 text-sm font-medium">
                {t("assessment.dashboard.videoProctoring")}
              </span>
              <GetHeroIcon
                name={
                  showProctoringVideoPreview
                    ? "ArrowsPointingInIcon"
                    : "ArrowsPointingOutIcon"
                }
                className="w-4 h-4"
              />
            </button>
          )}
          {/* Theme switcher */}
          {!viewHeader && <Switcher />}
          <div className="w-60">
            <LanguageSwitcher />
          </div>
          <ZoomControls compact={false} showLabel={true} />
          <Popover className="relative inline-block">
            <PopoverButton className="inline-flex items-center font-semibold ">
              <span className="flex ">
                <div className="flex items-center gap-1 cursor-pointer">
                  <GetHeroIcon
                    name="ExclamationCircleIcon"
                    variant="outline"
                    className="w-5 h-5 text-secondary"
                  />
                  <span>{t("common.help")}</span>
                </div>
              </span>
            </PopoverButton>
            <PopoverPanel
              transition
              className="absolute left-1/2 z-30 mt-3 flex w-screen max-w-min -translate-x-3/4 px-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            >
              {({ close }: any) => (
                <div className="w-56 p-3 text-base font-medium shadow-lg shrink rounded-xl bg-bkg text-secondary ring-1 ring-secondary/5">
                  <div
                    className="flex items-center gap-1 my-2 cursor-pointer"
                    onClick={() => {
                      setShowUITour(true);
                      close();
                    }}
                  >
                    <span>
                      <GetHeroIcon
                        name={"CursorArrowRaysIcon"}
                        variant="outline"
                        className="w-5 h-5 text-black  dark:text-white"
                      />
                    </span>
                    <span>{t("common.tour")}</span>
                  </div>
                  <hr />
                  <div
                    className="flex items-center gap-1 my-2 cursor-pointer"
                    onClick={() => setOpenModal(!openModal)}
                  >
                    <span>
                      <GetHeroIcon
                        name={"InformationCircleIcon"}
                        variant="outline"
                        className="w-5 h-5 text-black dark:text-white"
                      />
                    </span>
                    <span>{t("common.instructions")}</span>
                  </div>
                </div>
              )}
            </PopoverPanel>
          </Popover>
          <Button
            data-tour="ad-submitBtn"
            variant="contained"
            buttonColor="secondary"
            className="bg-red-500 hover:bg-red-500 dark:bg-red-700 dark:hover:bg-red-700 border-none"
            onClick={() => {
              dispatch(AssessmentSliceActions.mutateShowSubmissionModal(true));
            }}
          >
            {t("assessment.dashboard.submitFeature", {
              featureName: orgbasedFeatureName
            })}
          </Button>
        </div>
      </div>
      {/* Legends */}
      <div className="flex flex-col md:flex-row justify-between items-baseline">
        <div
          data-tour="ad-legend"
          className="flex items-center border border-border px-1.5 py-1 w-fit rounded-lg gap-5 mt-14 "
        >
          <LegendItem
            text={t("assessment.dashboard.legend.notStarted")}
            variant="secondary"
          />
          <LegendItem
            text={t("assessment.dashboard.legend.inProgress")}
            variant="amber"
          />
          <LegendItem
            text={t("assessment.dashboard.legend.completed")}
            variant="primary"
          />
        </div>
        <div onClick={toggleExpandAll} className="mt-5 md:mt-0">
          <Button
            label={
              expandAll
                ? t("assessment.dashboard.closeAllSections")
                : t("assessment.dashboard.expandAllSections")
            }
            variant="outlined"
            className="h-full"
          />
        </div>
      </div>
      {/* Sections */}
      <div className="mt-10 space-y-10">
        {ongoingSections &&
          ongoingSections.length > 0 &&
          ongoingSections.map((ongoingSection: OngoingSection) => {
            const sectionId = ongoingSection.sectionId;
            const sectionProgress = assessmentProgress[sectionId];
            const isManuallyToggled = sectionId in manuallyExpandedCards;

            const isExpanded = isManuallyToggled
              ? manuallyExpandedCards[sectionId]
              : expandAll;
            return (
              <AssessmentSectionCard
                key={ongoingSection.id}
                ongoingSectionId={sectionId}
                sectionProgress={sectionProgress}
                isExpanded={isExpanded}
                onToggle={() => toggleCard(sectionId)}
              />
            );
          })}
        {(!ongoingSections || ongoingSections.length === 0) && (
          <div className="flex flex-col items-center justify-center mt-20 space-y-2">
            <GetHeroIcon
              name="ExclamationTriangleIcon"
              variant="outline"
              className="w-5 h-5"
            />
            <span>{t("assessment.dashboard.noSectionsFound")}</span>
          </div>
        )}
      </div>
      <Modal
        open={openModal}
        children={<Instructions instructionType={val} />}
        setOpen={setOpenModal}
        dialogueClassName="w-[80vw]"
        closeBtn={false}
        childDialogTitle={t("common.instructions")}
      />
      <CustomTourProvider
        steps={getAssessmentSectionsDashboardSteps(t)}
        isTourOpen={showUITour}
        setIsTourOpen={setShowUITour}
      />
    </div>
  );
};

export default AssessmentDashboard;
